import React, { useEffect, useState } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowLeft } from '@mui/icons-material';

const StockRequestsList = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');
  const [receivedMap, setReceivedMap] = useState({});
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API.BASE_URL}material-request-indent`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          outlet: outletData?._id,
        },
      });

      const list = response?.data?.data?.data || [];

      setRequests(Array.isArray(list) ? list : []);
      const initialReceived = {};
      (list || []).forEach((req) => {
        (req.items || []).forEach((it) => {
          initialReceived[it._id] = it.recievedQuantity ?? '';
        });
      });
      setReceivedMap(initialReceived);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
        'Failed to fetch material request indents.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const statusColor = (status = '') => {
    const s = status.toLowerCase();
    if (s === 'approved') return 'success';
    if (s === 'rejected') return 'error';
    if (s === 'completed') return 'success';
    return 'warning'; // for PENDING / others
  };

  const handleReceivedChange = (itemId, value) => {
    if (value !== '' && isNaN(Number(value))) return;
    setReceivedMap((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const totalRequests = requests.length;
  const pendingCount = requests.filter(
    (r) => (r.status || 'PENDING').toUpperCase() === 'PENDING'
  ).length;
  const completedCount = requests.filter(
    (r) => (r.status || '').toUpperCase() === 'COMPLETED'
  ).length;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f3f4f7 0%, #eef2f7 100%)',
        minHeight: '100vh',
      }}
    >
      <Toolbar />
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: 3,
          pb: 4,
          pt: 5
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mt: 3,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
            <KeyboardArrowLeft sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
            <Box
              sx={(theme) => ({
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.light + '22',
                color: theme.palette.primary.main,
              })}
            >
              <Inventory2OutlinedIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: '#003049', fontSize: 24 }}
              >
                Stock Requistions List
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: 14, mt: 0.3 }}
              >
                Track all stock requests raised from this outlet.
              </Typography>
            </Box>
          </Box>

          {/* Summary chips */}
          {totalRequests > 0 && (
            <Stack direction="row" spacing={1}>
              <Chip
                size="medium"
                label={`Total: ${totalRequests}`}
                variant="outlined"
                sx={{ fontSize: 13, height: 30 }}
              />
              <Chip
                size="medium"
                label={`Pending: ${pendingCount}`}
                color={pendingCount > 0 ? 'warning' : 'default'}
                sx={{ fontSize: 13, height: 30 }}
              />
              <Chip
                size="medium"
                label={`Completed: ${completedCount}`}
                color={completedCount > 0 ? 'success' : 'default'}
                sx={{ fontSize: 13, height: 30 }}
              />
            </Stack>
          )}
        </Box>

        <Paper
          elevation={3}
          sx={(theme) => ({
            borderRadius: 3,
            p: 3,
            minHeight: '60vh',
            border: `1px solid ${theme.palette.divider}`,
            background: 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
          })}
        >
          {loading ? (
            <Box
              sx={{
                height: '50vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <CircularProgress size={30} />
              <Typography variant="body1" sx={{ mt: 1.8, fontSize: 14 }}>
                Fetching material request indents...
              </Typography>
            </Box>
          ) : requests.length === 0 ? (
            <Box
              sx={{
                height: '50vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" fontWeight={600} sx={{ fontSize: 15 }}>
                No material request indents found.
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.7, maxWidth: 360, fontSize: 13 }}
              >
                Once you raise stock requests from this outlet, they will appear
                here with their status and expected delivery.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.6}>
              {requests
                .slice()
                .reverse()
                .map((req) => {
                  const items = req.items || [];
                  const outlet = req.outlet || {};
                  const status = req.status || 'PENDING';
                  const upperStatus = status.toUpperCase();
                  const isApproved = upperStatus === 'APPROVED';

                  const handleOpenDetails = () => {
                    if (!isApproved) return;
                    navigate(`/stock-requistion-details`, {
                      state: { indent: req },
                    });
                  };

                  return (
                    <Box
                      key={req._id}
                      component={Paper}
                      onClick={isApproved ? handleOpenDetails : undefined}
                      elevation={0}
                      sx={(theme) => ({
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: '#ffffff',
                        p: 2,
                        cursor: isApproved ? 'pointer' : 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flexWrap: 'wrap',
                        transition: 'all 0.15s ease-out',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                        '&:hover': isApproved
                          ? {
                            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
                            borderColor: theme.palette.primary.light,
                            transform: 'translateY(-1px)',
                          }
                          : {},
                      })}
                    >
                      {/* Top row: indent info + status */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Left section: indent info */}
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mb: 0.4, fontSize: 15 }}
                          >
                            {req.indentNumber}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: 13 }}
                          >
                            Fin. Year: <strong>{req.fin_year || '-'}</strong>
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: 13, mt: 0.2 }}
                          >
                            Created on: {formatDate(req.createdAt)}
                          </Typography>
                        </Box>

                        {/* Right section: status, outlet, item count */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.6,
                            minWidth: 230,
                          }}
                        >
                          <Stack direction="row" spacing={0.8}>
                            <Chip
                              size="small"
                              label={upperStatus}
                              color={statusColor(upperStatus)}
                              sx={{
                                fontSize: 11.5,
                                height: 24,
                                textTransform: 'uppercase',
                                fontWeight: 600,
                              }}
                            />
                            <Chip
                              size="small"
                              label={`${items.length} item${items.length !== 1 ? 's' : ''
                                }`}
                              variant="outlined"
                              sx={{ fontSize: 11.5, height: 24 }}
                            />
                          </Stack>
                          {outlet?.name && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.3,
                                textAlign: 'right',
                                fontSize: 13,
                              }}
                            >
                              {outlet.name}
                              {outlet.city ? ` • ${outlet.city}` : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* For non-approved requests, show an accordion with details */}
                      {!isApproved && (
                        <Box sx={{ width: '100%', mt: 0.5 }}>
                          <Accordion
                            elevation={0}
                            sx={(theme) => ({
                              backgroundColor: 'transparent',
                              '&::before': { display: 'none' },
                              borderRadius: 1.5,
                              border: `1px dashed ${theme.palette.divider}`,
                            })}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                minHeight: 36,
                                '& .MuiAccordionSummary-content': {
                                  my: 0.2,
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: 13, fontWeight: 500 }}
                              >
                                View requested items & quantities
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0.5 }}>
                              {items.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: 13 }}
                                >
                                  No items found for this indent.
                                </Typography>
                              ) : (
                                <Stack
                                  spacing={0.75}
                                  divider={
                                    <Divider
                                      flexItem
                                      sx={{
                                        borderStyle: 'dashed',
                                        opacity: 0.4,
                                      }}
                                    />
                                  }
                                >
                                  {items.map((it) => {
                                    const rm = it.rawMaterial || {};
                                    const rmName = rm.name || '-';
                                    const unit = rm.unit || '';
                                    const qty =
                                      it.requestedQuantity ?? '-';
                                    const itemStatus = (
                                      it.status || 'PENDING'
                                    ).toUpperCase();

                                    return (
                                      <Box
                                        key={it._id}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          gap: 1,
                                          fontSize: 14,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            flex: 1.2,
                                            pr: 1.2,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: 14,
                                              fontWeight: 500,
                                            }}
                                          >
                                            • {rmName}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: 12.5, mt: 0.2 }}
                                          >
                                            Requested: {qty} {unit}
                                          </Typography>
                                          {it.expectedDeliveryDate && (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ mt: 0.1, fontSize: 12 }}
                                            >
                                              Est. delivery:{' '}
                                              {formatDate(
                                                it.expectedDeliveryDate
                                              )}
                                            </Typography>
                                          )}
                                        </Box>

                                        <Box
                                          sx={{
                                            minWidth: 120,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                          }}
                                        >
                                          <Chip
                                            size="small"
                                            label={itemStatus}
                                            color={statusColor(itemStatus)}
                                            sx={{
                                              fontSize: 11,
                                              height: 22,
                                              textTransform: 'uppercase',
                                              fontWeight: 600,
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StockRequestsList;
